/**
 * Velocity Tier Selector
 *
 * Human-friendly delivery speed tier selector for contract-based delivery.
 * Clients select delivery velocity (pacing priority).
 *
 * PRINCIPLES:
 * - Focus on outcomes (delivery speed)
 * - Simple, intuitive selection
 */

import { cn } from "@/lib/utils";
import { Gauge, Zap, Rocket, Snail } from "lucide-react";
import {
  type VelocityTier,
  bidToVelocityTier,
  velocityTierToBid,
} from "@/lib/velocityTier";

interface VelocityTierSelectorProps {
  value: VelocityTier;
  onChange: (tier: VelocityTier) => void;
  disabled?: boolean;
  className?: string;
}

interface TierOption {
  id: VelocityTier;
  label: string;
  description: string;
  icon: typeof Gauge;
}

const tiers: TierOption[] = [
  {
    id: 'conservative',
    label: 'Conservative',
    description: 'Steady pace, lower priority',
    icon: Snail,
  },
  {
    id: 'balanced',
    label: 'Balanced',
    description: 'Standard delivery speed',
    icon: Gauge,
  },
  {
    id: 'aggressive',
    label: 'Aggressive',
    description: 'Faster delivery',
    icon: Zap,
  },
  {
    id: 'maximum',
    label: 'Maximum',
    description: 'Highest priority',
    icon: Rocket,
  },
];

export { bidToVelocityTier, velocityTierToBid };

export function VelocityTierSelector({ 
  value, 
  onChange, 
  disabled = false,
  className 
}: VelocityTierSelectorProps) {
  return (
    <div className={cn("grid grid-cols-2 sm:grid-cols-4 gap-2", className)}>
      {tiers.map((tier) => {
        const Icon = tier.icon;
        const isSelected = value === tier.id;
        
        return (
          <button
            key={tier.id}
            type="button"
            onClick={() => onChange(tier.id)}
            disabled={disabled}
            className={cn(
              "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              isSelected
                ? "border-primary bg-primary/5 text-primary"
                : "border-border bg-card hover:border-primary/40 hover:bg-muted/50",
              disabled && "opacity-50 cursor-not-allowed hover:border-border hover:bg-card"
            )}
          >
            <Icon className={cn(
              "w-6 h-6",
              isSelected ? "text-primary" : "text-muted-foreground"
            )} />
            <div className="text-center">
              <p className={cn(
                "text-sm font-medium",
                isSelected ? "text-primary" : "text-foreground"
              )}>
                {tier.label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {tier.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
